import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getBaseUrl } from '../utils/getBaseUrl';
import { getCurrentUser } from '../utils/auth';

const BrandingContext = createContext();

const isLmsCampus = () => {
  const h = window.location.hostname;
  return h.endsWith('lmscampus.com') || h === 'localhost';
};

/**
 * Extract subdomain from current hostname.
 * e.g. "abc-academy.lmscampus.com" → "abc-academy"
 * Returns null for bare domain, localhost, or www.
 */
const getSubdomain = () => {
  const h = window.location.hostname;
  if (h === 'localhost') return null;
  const baseDomain = 'lmscampus.com';
  if (!h.endsWith(`.${baseDomain}`)) return null;
  const sub = h.slice(0, h.length - baseDomain.length - 1);
  if (!sub || ['www', 'app', 'api', 'admin'].includes(sub)) return null;
  return sub;
};

const DEFAULTS = {
  brandName: isLmsCampus() ? 'LMS Campus' : 'Visible Campus',
  brandSubtitle: 'Learning Management',
  primaryColor: '#2563eb',
  logoBgColor: '#3b82f6',
  logoUrl: null,
  faviconUrl: null,
  supportEmail: '',
};

// ── Branding cache helpers ──────────────────────────────────
const CACHE_KEY = `vc-branding-${window.location.hostname}`;
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

const getCachedBranding = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    // Return cached data even if expired — we'll refresh in background
    return { data, expired: Date.now() - timestamp > CACHE_TTL };
  } catch {
    return null;
  }
};

const setCachedBranding = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {}
};

/**
 * Build axios headers for branding requests.
 * Mirrors apiClient's X-Custom-Domain logic so the backend tenantResolver
 * can reliably resolve the institute even before the user has logged in.
 */
const getBrandingHeaders = () => {
  const hostname = window.location.hostname;
  if (
    hostname !== 'localhost' &&
    !hostname.endsWith('lmscampus.com') &&
    !hostname.endsWith('visiblecampus.com')
  ) {
    return { 'X-Custom-Domain': hostname };
  }
  return {};
};

/**
 * Update theme-color meta tag based on branding.
 */
const updateThemeColor = (themeColor) => {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.content = themeColor;
};

/**
 * Dynamically update the browser tab favicon.
 */
const updateFavicon = (url) => {
  if (!url) return;
  let link = document.querySelector('link[rel="icon"]');
  if (link) {
    link.href = url;
  } else {
    link = document.createElement('link');
    link.rel = 'icon';
    link.href = url;
    document.head.appendChild(link);
  }
  // Also update apple-touch-icon for mobile "Add to Home Screen"
  let appleLink = document.querySelector('link[rel="apple-touch-icon"]');
  if (appleLink) {
    appleLink.href = url;
  }
};

/**
 * Apply branding to DOM — title, theme color, and favicon.
 */
const applyBrandingToDOM = (b) => {
  document.title = b.brandName || DEFAULTS.brandName;
  updateThemeColor(b.primaryColor || DEFAULTS.primaryColor);
  if (b.faviconUrl) updateFavicon(b.faviconUrl);
  else if (b.logoUrl) updateFavicon(b.logoUrl);
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  // Initialize from cache immediately (stale-while-revalidate pattern)
  const cached = getCachedBranding();
  const [branding, setBranding] = useState(cached?.data || DEFAULTS);
  const [loading, setLoading] = useState(!cached?.data);

  // Apply cached branding to DOM immediately (before any API call)
  useEffect(() => {
    if (cached?.data) applyBrandingToDOM(cached.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mergeBranding = useCallback((platform, institute) => {
    const hasCustomBranding = !!(institute.brandName || institute.logoUrl || institute.faviconUrl);
    const domainBrandName = isLmsCampus() ? DEFAULTS.brandName : null;

    return {
      brandName: (hasCustomBranding && institute.brandName) || domainBrandName || platform.brandName || DEFAULTS.brandName,
      brandSubtitle: (hasCustomBranding && institute.brandSubtitle) || platform.brandSubtitle || DEFAULTS.brandSubtitle,
      primaryColor: (hasCustomBranding && institute.primaryColor) || platform.primaryColor || DEFAULTS.primaryColor,
      logoBgColor: (hasCustomBranding && institute.logoBgColor) || platform.logoBgColor || DEFAULTS.logoBgColor,
      logoUrl: (hasCustomBranding && institute.logoUrl) || platform.logoUrl || null,
      faviconUrl: (hasCustomBranding && institute.faviconUrl) || platform.faviconUrl || null,
      supportEmail: platform.supportEmail || DEFAULTS.supportEmail,
    };
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      const headers = getBrandingHeaders();

      // 1. Fetch platform-level branding
      let platform = {};
      try {
        const { data } = await axios.get(`${getBaseUrl()}/platform/branding`, { headers });
        if (data?.branding) platform = data.branding;
      } catch {}

      // 2. Fetch institute-level branding
      //    Always try on subdomains or custom domains (even without login)
      //    tenantResolver resolves from X-Custom-Domain header / Origin / hostname
      let institute = {};
      const user = getCurrentUser();
      const rawInstId = user?.instituteId;
      const instId = rawInstId && typeof rawInstId === 'object' ? (rawInstId._id || rawInstId) : rawInstId;
      const subdomain = getSubdomain();
      const isCustomDomain = !isLmsCampus() && window.location.hostname !== 'localhost';

      if (instId || subdomain || isCustomDomain) {
        try {
          const url = instId
            ? `${getBaseUrl()}/institute/branding?instituteId=${instId}`
            : `${getBaseUrl()}/institute/branding`;
          const { data } = await axios.get(url, { headers });
          if (data?.branding) institute = data.branding;
        } catch {}
      }

      // 3. Merge and apply
      const merged = mergeBranding(platform, institute);
      setBranding(merged);
      applyBrandingToDOM(merged);

      // 4. Cache the result (exclude presigned URLs that expire within 1h — they'll refresh on next load)
      setCachedBranding(merged);
    } catch (err) {
      console.warn('Failed to fetch branding, using defaults:', err.message);
    } finally {
      setLoading(false);
    }
  }, [mergeBranding]);

  useEffect(() => {
    // If cached data exists and isn't expired, skip immediate fetch (background refresh)
    if (cached?.data && !cached.expired) {
      setLoading(false);
      // Still refresh in background after a short delay to catch any updates
      const timer = setTimeout(fetchBranding, 3000);
      return () => clearTimeout(timer);
    }
    // No cache or expired — fetch immediately
    fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBranding]);

  const contextValue = useMemo(() => ({
    ...branding, loading, refreshBranding: fetchBranding,
  }), [branding, loading, fetchBranding]);

  return (
    <BrandingContext.Provider value={contextValue}>
      {children}
    </BrandingContext.Provider>
  );
};
